const ZXING_FORMAT_MAP = {
    aztec: 'AZTEC',
    codabar: 'CODABAR',
    code_128: 'CODE_128',
    code_39: 'CODE_39',
    code_93: 'CODE_93',
    data_matrix: 'DATA_MATRIX',
    ean_13: 'EAN_13',
    ean_8: 'EAN_8',
    itf: 'ITF',
    pdf417: 'PDF_417',
    qr_code: 'QR_CODE',
    upc_a: 'UPC_A',
    upc_e: 'UPC_E',
};

export const WAREHOUSE_BARCODE_FORMATS = [
    'qr_code',
    'data_matrix',
    'code_128',
    'code_39',
    'ean_13',
    'ean_8',
    'itf',
];

export const QR_SEARCH_FORMATS = ['qr_code', 'code_128', 'data_matrix'];

const getCameraErrorMessage = (error) => {
    if (error?.name === 'NotAllowedError') {
        return 'Разрешите доступ к камере в настройках браузера';
    }
    if (error?.name === 'NotFoundError') {
        return 'Камера не найдена';
    }
    return error?.message || 'Не удалось открыть камеру';
};

const prepareVideo = async (video, stream) => {
    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', 'true');
    video.setAttribute('webkit-playsinline', 'true');
    video.setAttribute('autoplay', 'true');
    await video.play();
};

const applyCameraEnhancements = async (stream) => {
    const track = stream.getVideoTracks()[0];
    if (!track) return;

    try {
        const capabilities = track.getCapabilities?.() || {};
        const constraints = {};

        if (capabilities.focusMode?.includes('continuous')) {
            constraints.focusMode = 'continuous';
        }
        if (capabilities.exposureMode?.includes('continuous')) {
            constraints.exposureMode = 'continuous';
        }
        if (capabilities.whiteBalanceMode?.includes('continuous')) {
            constraints.whiteBalanceMode = 'continuous';
        }
        if (capabilities.zoom) {
            const min = capabilities.zoom.min || 1;
            const max = capabilities.zoom.max || min;
            constraints.zoom = Math.min(max, Math.max(min, 1.4));
        }
        if (capabilities.pointsOfInterest) {
            constraints.pointsOfInterest = [{ x: 0.5, y: 0.5 }];
        }

        if (Object.keys(constraints).length) {
            await track.applyConstraints({ advanced: [constraints] });
        }
    } catch {
        // Не все телефоны принимают дополнительные ограничения камеры.
    }
};

const createNativeDetector = async (formats) => {
    if (typeof window.BarcodeDetector !== 'function') return null;

    try {
        const supported = await window.BarcodeDetector.getSupportedFormats();
        const matched = formats.filter((format) => supported.includes(format));
        if (!matched.length) return null;
        return new window.BarcodeDetector({ formats: matched });
    } catch {
        return null;
    }
};

const createScanCanvas = () => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { willReadFrequently: true });
    return { canvas, context };
};

const drawScanRegion = (video, canvas, context, size) => {
    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height || !context) return false;

    const side = Math.min(width, height) * 0.78;
    const sx = (width - side) / 2;
    const sy = (height - side) / 2;
    if (canvas.width !== size || canvas.height !== size) {
        canvas.width = size;
        canvas.height = size;
    }
    context.drawImage(video, sx, sy, side, side, 0, 0, size, size);
    return true;
};

const createZxingReader = async (formats) => {
    const [{ BrowserMultiFormatReader }, zxingLib] = await Promise.all([
        import('@zxing/browser'),
        import('@zxing/library'),
    ]);
    const hints = new Map();
    const mapped = formats
        .map((format) => zxingLib.BarcodeFormat[ZXING_FORMAT_MAP[format]])
        .filter((value) => value != null);

    if (mapped.length) {
        hints.set(zxingLib.DecodeHintType.POSSIBLE_FORMATS, mapped);
    }
    hints.set(zxingLib.DecodeHintType.TRY_HARDER, false);
    if (zxingLib.DecodeHintType.ALSO_INVERTED != null) {
        hints.set(zxingLib.DecodeHintType.ALSO_INVERTED, true);
    }

    return new BrowserMultiFormatReader(hints, {
        delayBetweenScanAttempts: 80,
        delayBetweenScanSuccess: 250,
    });
};

export const startCameraBarcodeScanner = async ({
    video,
    formats = WAREHOUSE_BARCODE_FORMATS,
    onDetect,
    onReady,
    onError,
}) => {
    let stopped = false;
    let rafId = 0;
    let stream = null;

    const stop = () => {
        stopped = true;
        window.cancelAnimationFrame(rafId);
        stream?.getTracks().forEach((track) => track.stop());
        stream = null;
        if (video) {
            video.pause();
            video.srcObject = null;
        }
    };

    try {
        if (!navigator.mediaDevices?.getUserMedia) {
            throw new Error('Камера не поддерживается браузером');
        }
        if (!video) {
            throw new Error('Видеоэлемент не найден');
        }

        stream = await navigator.mediaDevices.getUserMedia({
            audio: false,
            video: {
                facingMode: { ideal: 'environment' },
                width: { ideal: 1280 },
                height: { ideal: 720 },
                frameRate: { ideal: 24, max: 30 },
            },
        });

        if (stopped) {
            stream.getTracks().forEach((track) => track.stop());
            return stop;
        }

        await prepareVideo(video, stream);
        await applyCameraEnhancements(stream);

        const nativeDetector = await createNativeDetector(formats);
        const scanCanvas = nativeDetector ? null : createScanCanvas();
        const zxingReader = nativeDetector ? null : await createZxingReader(formats);
        let detecting = false;

        const tick = async () => {
            if (stopped) return;
            rafId = window.requestAnimationFrame(tick);
            if (detecting || video.readyState < 2) return;

            detecting = true;
            try {
                let text = '';
                if (nativeDetector) {
                    const barcodes = await nativeDetector.detect(video);
                    text = String(barcodes[0]?.rawValue || '').trim();
                } else if (
                    zxingReader &&
                    scanCanvas &&
                    drawScanRegion(video, scanCanvas.canvas, scanCanvas.context, 480)
                ) {
                    try {
                        const result = zxingReader.decodeFromCanvas(scanCanvas.canvas);
                        text = String(result?.getText?.() || '').trim();
                    } catch {
                        // На этом кадре код не найден.
                    }
                }
                if (text && !stopped) {
                    onDetect?.(text);
                }
            } catch {
                // Пустой или ещё не готовый кадр — просто ждём следующий.
            } finally {
                detecting = false;
            }
        };

        rafId = window.requestAnimationFrame(tick);
        onReady?.(nativeDetector ? 'native' : 'zxing');
        return stop;
    } catch (error) {
        stop();
        const wrapped = new Error(getCameraErrorMessage(error));
        wrapped.cause = error;
        wrapped.name = error?.name || 'CameraError';
        onError?.(wrapped);
        return stop;
    }
};
